/**
 * actionDisabledReason — why a rule action is greyed out in the picker.
 * Split out of rule-action-compat so both files stay inside the size limit:
 * that one holds the compatibility MODEL, this one the human copy for it.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { RuleAction } from '@trafficpulse/shared';

import { RULE_ACTION_META } from '../rule-action-meta';
import { ROTATION_VERBS, isActionDisabled } from '../rule-action-compat';

const isRotationVerb = (a: RuleAction): boolean => ROTATION_VERBS.includes(a);

/** The standalone primary currently blocking others, if any. */
function soloVerb(current: RuleAction[]): 'exclude' | 'weight' | null {
  if (current.includes('exclude')) return 'exclude';
  if (current.includes('weight')) return 'weight';
  return null;
}

const SOLO_LABEL = { exclude: 'Exclude', weight: 'Weight' } as const;

/** Why an action is greyed out (tooltip / sub-label), or undefined when enabled. */
export function actionDisabledReason(
  current: RuleAction[],
  action: RuleAction,
): string | undefined {
  if (RULE_ACTION_META[action]?.soon) return 'Coming soon.';
  if (!isActionDisabled(current, action)) return undefined;
  const solo = soloVerb(current);
  if (action === 'exclude' || action === 'weight') {
    return solo
      ? `${SOLO_LABEL[solo]} runs alone — clear it first.`
      : `${SOLO_LABEL[action === 'exclude' ? 'exclude' : 'weight']} runs alone — clear the rotation actions to use it.`;
  }
  if (isRotationVerb(action)) {
    return solo
      ? `${SOLO_LABEL[solo]} runs alone — clear it to use rotation actions.`
      : 'The rotation actions can’t combine — pick one.';
  }
  // add-element
  if (current.includes('reactivate')) {
    return 'Reactivate brings elements back — nothing is freed to backfill.';
  }
  return solo
    ? `${SOLO_LABEL[solo]} runs alone.`
    : 'Pick Set to queue, Eliminate or Pause first — this backfills the freed slot.';
}
